import
{
  Component, OnInit, ViewChildren, QueryList, Injector, AfterViewInit, ComponentRef,
  input, ChangeDetectorRef, 
  ChangeDetectionStrategy
} from '@angular/core';

import { DynamicHost } from '../../../../directives/dynamic-host';
import { ClientService } from '../../../../services/client-service';

import { MatTabsModule, MatTabChangeEvent } from '@angular/material/tabs';

import { ActiveForms, ICentralNote, IContactEvent, ITask } from '../../../../models/client';
import { FormItemsComponent } from '../form-items-component/form-items-component';
import { ContactEventsComponent } from '../contact-events-component/contact-events-component';
import { TasksComponent } from '../tasks-component/tasks-component';
import { CentralNotesComponent } from '../central-notes-component/central-notes-component';
import { DocumentsComponent } from '../documents-component/documents-component';
import { TimelineComponent } from '../timeline-component/timeline-component';
import { HealthwiseComponent } from '../healthwise-component/healthwise-component';
import { DiyaHealthComponent } from '../diya-health-component/diya-health-component';


const componentMap: Record<string, any> = {
  FormItemsComponent,
  ContactEventsComponent,
  TasksComponent,
  CentralNotesComponent,
  DocumentsComponent,
  TimelineComponent,
  HealthwiseComponent,
  DiyaHealthComponent,
};

interface TabData
{
  label: string;                        // The label for the tab
  content: {
    component: string;                  // The name of the component to load dynamically
    params?: any;       // Optional parameters to pass to the component
  };
}

@Component( {
  selector: 'app-vertical-tabs',
  imports: [MatTabsModule, DynamicHost],
  templateUrl: './vertical-tabs.html',
  styleUrls: ['./vertical-tabs.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class VerticalTabsComponent implements OnInit, AfterViewInit
{

  @ViewChildren( DynamicHost ) dynamicHosts!: QueryList<DynamicHost>;

  constructor(
    private injector: Injector,
    private _clientService: ClientService,
    private _cdr: ChangeDetectorRef ){}

  clientId = input.required<string>();
  activeForms: ActiveForms | undefined;
  inActiveForms: ActiveForms | undefined;
  tabs: TabData[] = [];
  visibleTabs: string[] = []; 

  async ngOnInit(): Promise<void>
  {
    this.activeForms = await this._clientService.getActiveForms( this.clientId() ) || { all: {} };
    this.inActiveForms = await this._clientService.getArchivedForms( this.clientId() ) || { all: {} };
    this.visibleTabs = await this._clientService.getVisitbleTabs( this.clientId() ) || [];
   

    // Initialize tabs with their labels and corresponding component names
    this.tabs = [
      { label: "Pathways", content: { component: "FormItemsComponent", params: { type: ['P'], data: this.activeForms, addEnabled: true, readOnly: false } } },
      { label: "Checklists", content: { component: "FormItemsComponent", params: { type: ['C'], data: this.activeForms, addEnabled: true, readOnly: false } } },
      { label: "Tools", content: { component: "FormItemsComponent", params: { type: ['T'], data: this.activeForms, addEnabled: true, readOnly: false } } },
      { label: "Health Homes", content: { component: "FormItemsComponent", params: { type: ['T'], data: this.activeForms, addEnabled: true, readOnly: false } } },
      { label: "Care Plan", content: { component: "FormItemsComponent", params: { type: ['P', 'C', 'T', 'T-HC'], data: this.activeForms, addEnabled: true, readOnly: false } } },
      { label: "PW Certified", content: { component: "FormItemsComponent", params: { type: ['P', 'C', 'T'], data: this.activeForms, addEnabled: true, readOnly: false } } },
      { label: "Archive", content: { component: "FormItemsComponent", params: { type: ['P', 'C', 'T'], data: this.inActiveForms, addEnabled:false ,readOnly: true } } },
      { label: "Contacts", content: { component: "ContactEventsComponent", params: { type: ['Contact'], data: this.getContactEvents() } } },
      { label: "Tasks", content: { component: "TasksComponent", params: { type: ['Tasks'], data: this.getTasks() } } },
      { label: "Central Notes", content: { component: "CentralNotesComponent" } },
      { label: "Documents", content: { component: "DocumentsComponent" } },
      { label: "Timeline", content: { component: "TimelineComponent" } },
      { label: "Healthwise", content: { component: "HealthwiseComponent" } },
      { label: "PHR", content: { component: "DiyaHealthComponent" } },
    ];

    this._cdr.detectChanges();
  }

  async ngAfterViewInit(): Promise<void>
  {
    this.loadActiveTabComponent( this.tabs[0] );
  }

  async onTabChange( event: MatTabChangeEvent ): Promise<void>
  {
    const tab = this.tabs.find( t => t.label === event.tab.textLabel );
    if ( !tab ) return;

    await this.loadActiveTabComponent( tab );
  }

  async getContactEvents(): Promise<IContactEvent[]>
  {
    return await this._clientService.getContactEvents( this.clientId() ) ||[];
  }

  async getTasks(): Promise<ITask[]>
  {
    return await this._clientService.getTasks( this.clientId() ) || [];
  }

  async getCentralNotes(): Promise<ICentralNote[]>
  {
    return await this._clientService.getCentralNotes( this.clientId() ) || [];
  }

  ActiveTabs(): TabData[]
  {
    return this.tabs.filter( tab => this.visibleTabs.includes( tab.label ) );
  } 

  async loadActiveTabComponent( activeTab: TabData ): Promise<void>
  {
    this._cdr.detectChanges();

    if ( !this.dynamicHosts || this.dynamicHosts.length === 0 ) return;

    // Assuming only one host for simplicity
    const host = this.dynamicHosts.first;

    // Clear previous component
    host.viewContainerRef.clear();

    const componentType = componentMap[activeTab.content.component];
    if ( componentType )
    {
      const componentRef: ComponentRef<any> = host.viewContainerRef.createComponent( componentType, {
        injector: this.injector
      } );

      // Pass input
      componentRef.instance.clientId = this.clientId;
      
      if ( componentRef.instance.hasOwnProperty( 'data' ) )
      {
        componentRef.instance.data = await activeTab.content.params.data;
      }

      if ( componentRef.instance.hasOwnProperty( 'formTypes' ) )
      {
        componentRef.instance.formTypes = activeTab.content.params.type;
      }
      if ( componentRef.instance.hasOwnProperty( 'addEnabled' ) )
      {
        componentRef.instance.addEnabled = activeTab.content.params.addEnabled;
      }
      if ( componentRef.instance.hasOwnProperty( 'readOnly' ) )
      {
        componentRef.instance.readOnly = activeTab.content.params.readOnly;
      }

    }

  }
  
}
